/**
 * Sponsor instances at /app/integrations. Credentials go to Key Vault;
 * this page never shows them again. Product nouns stay off the copy.
 */
import { useState } from "preact/hooks";
import { cap, fmtStamp } from "@trellis/core";

export interface IntegrationLine {
  id: string;
  kind: string;
  deployment?: string;
  baseUrl?: string;
  displayName: string;
  createdBy?: string;
  createdAt?: string;
  /** How many contracts point a project or a space at this instance. Nothing is read until one does. */
  links?: number;
  unreachable?: boolean;
  error?: string;
  lastOk?: string;
}

export interface IntegrationEdit { displayName?: string; baseUrl?: string; deployment?: string; credential?: string }

export interface IntegrationsScreenProps {
  entries: IntegrationLine[];
  /** These resolve false when the request was refused, so a form keeps what was typed. */
  onAdd: (input: { kind: string; deployment: string; baseUrl: string; displayName: string; credential: string }) => Promise<boolean | void>;
  onEdit: (id: string, input: IntegrationEdit) => Promise<boolean | void>;
  onRemove: (id: string) => Promise<boolean | void>;
  onTest: (id: string) => Promise<void>;
  notice?: { text: string; error?: boolean } | undefined;
}

/** Edit one instance in place. A blank credential leaves the stored one alone. */
function EditInstance({ entry, onEdit, onCancel }: { entry: IntegrationLine; onEdit: (id: string, input: IntegrationEdit) => Promise<boolean | void>; onCancel: () => void }) {
  const [displayName, setDisplayName] = useState(entry.displayName);
  const [baseUrl, setBaseUrl] = useState(entry.baseUrl ?? "");
  const [deployment, setDeployment] = useState(entry.deployment ?? "cloud");
  const [credential, setCredential] = useState("");
  const [busy, setBusy] = useState(false);
  const change: IntegrationEdit = {
    ...(displayName.trim() && displayName.trim() !== entry.displayName ? { displayName: displayName.trim() } : {}),
    ...(baseUrl.trim() && baseUrl.trim() !== entry.baseUrl ? { baseUrl: baseUrl.trim() } : {}),
    ...(deployment !== entry.deployment ? { deployment } : {}),
    ...(credential.trim() ? { credential: credential.trim() } : {}),
  };
  // An unchanged form used to PATCH `{}`, which the API refuses with "nothing to change".
  const changed = Object.keys(change).length > 0;
  const save = async () => {
    setBusy(true);
    try { await onEdit(entry.id, change); }
    finally { setBusy(false); }
  };
  return (
    <div class="inline-form">
      <label class="sr-only" for={`name-${entry.id}`}>Display name</label>
      <input id={`name-${entry.id}`} value={displayName} onInput={(e) => setDisplayName((e.target as HTMLInputElement).value)} />
      <label class="sr-only" for={`url-${entry.id}`}>Address</label>
      <input id={`url-${entry.id}`} value={baseUrl} placeholder="https://…" onInput={(e) => setBaseUrl((e.target as HTMLInputElement).value)} />
      <label class="sr-only" for={`dep-${entry.id}`}>Deployment</label>
      <select id={`dep-${entry.id}`} value={deployment} onChange={(e) => setDeployment((e.target as HTMLSelectElement).value)}>
        <option value="cloud">Cloud</option>
        <option value="datacenter">Data Center</option>
      </select>
      <label class="sr-only" for={`cred-${entry.id}`}>Replacement credential</label>
      <input id={`cred-${entry.id}`} type="password" value={credential} placeholder="Replace the credential (optional)" onInput={(e) => setCredential((e.target as HTMLInputElement).value)} />
      <button class="btn" disabled={busy || !changed} onClick={() => void save()}>Save</button>
      <button class="btn quiet" disabled={busy} onClick={onCancel}>Cancel</button>
      {!changed && <span class="hint">Change a field to save.</span>}
    </div>
  );
}

function kindLabel(kind: string): string {
  return kind === "jira" ? "ticket instance" : kind === "confluence" ? "page instance" : kind.replace(/-/g, " ");
}

/** The same two words the deployment select offers, so the row and the form agree. */
function deploymentLabel(deployment: string | undefined): string {
  return deployment === "datacenter" ? "Data Center" : deployment === "cloud" ? "Cloud" : deployment ?? "";
}

export function IntegrationsScreen({ entries, onAdd, onEdit, onRemove, onTest, notice }: IntegrationsScreenProps) {
  const [kind, setKind] = useState("jira");
  const [deployment, setDeployment] = useState("cloud");
  const [baseUrl, setBaseUrl] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [credential, setCredential] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | undefined>(undefined);
  const [busyId, setBusyId] = useState<string | undefined>(undefined);
  const [confirming, setConfirming] = useState<string | undefined>(undefined);
  const run = async (id: string, fn: () => Promise<void>) => {
    setBusyId(id);
    try { await fn(); } finally { setBusyId(undefined); }
  };
  const ok = baseUrl.trim() && displayName.trim() && credential.trim();
  const add = async () => {
    if (!ok) return;
    setBusy(true);
    try {
      const ok = await onAdd({ kind, deployment, baseUrl: baseUrl.trim(), displayName: displayName.trim(), credential: credential.trim() });
      if (ok !== false) { setBaseUrl(""); setDisplayName(""); setCredential(""); }
    } finally { setBusy(false); }
  };
  return (
    <main class="main settings">
      <div class="page-head">
        <span class="label">Sponsor · instances</span>
        <h1 class="h-lg">Linked systems</h1>
        <p class="lede">A credential is written to Key Vault and never stored here. Role holders attach a project, space, or team from the program's Linked card.</p>
        {notice && <div class={`notice${notice.error ? " error" : ""}`}>{notice.text}</div>}
      </div>
      <section class="panel">
        <div class="panel-head"><span class="label">Instances</span></div>
        {entries.length === 0 && <div class="line"><span class="dot off" /><span class="body muted">None yet.</span></div>}
        {entries.map((e) => (
          <div key={e.id}>
            <div class="line">
              <span class={`dot ${e.unreachable ? "bad" : e.links === 0 ? "watch" : e.lastOk ? "good" : "off"}`} />
              <span class="body">{e.displayName}
                <div class="s">{[cap(kindLabel(e.kind)), deploymentLabel(e.deployment), e.baseUrl, e.lastOk ? `last ok ${fmtStamp(e.lastOk)}` : "not polled", e.unreachable ? (e.error ?? "unreachable") : ""].filter(Boolean).join(" · ")}</div>
                {e.links === 0 && !e.unreachable && (
                  <div class="s tone-watch">The credential works and nothing is linked to it, so Trellis reads nothing from here. A contract links a project or a space on its own page, under Linked.</div>
                )}
                {e.links !== undefined && e.links > 0 && (
                  <div class="s dim">{e.links === 1 ? "1 project or space is linked to this instance." : `${e.links} projects or spaces are linked to this instance.`}</div>
                )}
                {confirming === e.id && <div class="s">The credential is deleted from Key Vault. Linked projects must be detached first.</div>}
              </span>
              <span class="row-actions">
                <button class="btn quiet" disabled={busyId === e.id} onClick={() => void run(e.id, () => onTest(e.id))}>Test</button>
                <button class="btn quiet" aria-expanded={editing === e.id} onClick={() => setEditing(editing === e.id ? undefined : e.id)}>Edit</button>
                {confirming === e.id ? (
                  <>
                    <button class="btn" disabled={busyId === e.id} onClick={() => void run(e.id, async () => { if (await onRemove(e.id) !== false) setConfirming(undefined); })}>Confirm remove</button>
                    <button class="btn quiet" onClick={() => setConfirming(undefined)}>Keep</button>
                  </>
                ) : (
                  <button class="btn quiet" disabled={busyId === e.id} onClick={() => setConfirming(e.id)}>Remove</button>
                )}
              </span>
            </div>
            {editing === e.id && <EditInstance entry={e} onEdit={async (id, input) => { const ok = await onEdit(id, input); if (ok !== false) setEditing(undefined); return ok; }} onCancel={() => setEditing(undefined)} />}
          </div>
        ))}
      </section>
      <section class="panel">
        <div class="panel-head"><span class="label">Add an instance</span></div>
        <div class="inline-form">
          <label class="sr-only" for="add-kind">What kind of instance</label>
          <select id="add-kind" value={kind} onChange={(e) => setKind((e.target as HTMLSelectElement).value)}>
            <option value="jira">Ticket instance</option>
            <option value="confluence">Page instance</option>
          </select>
          <label class="sr-only" for="add-deployment">Deployment</label>
          <select id="add-deployment" value={deployment} onChange={(e) => setDeployment((e.target as HTMLSelectElement).value)}>
            <option value="cloud">Cloud</option>
            <option value="datacenter">Data Center</option>
          </select>
          <label class="sr-only" for="add-name">Display name</label>
          <input id="add-name" value={displayName} placeholder="Display name" onInput={(e) => setDisplayName((e.target as HTMLInputElement).value)} />
          <label class="sr-only" for="add-url">Address</label>
          <input id="add-url" value={baseUrl} placeholder="https://…" onInput={(e) => setBaseUrl((e.target as HTMLInputElement).value)} />
          <label class="sr-only" for="add-cred">Credential</label>
          <input id="add-cred" type="password" value={credential} placeholder={deployment === "cloud" ? '{"email":"…","token":"…"}' : "PAT"} onInput={(e) => setCredential((e.target as HTMLInputElement).value)} />
          <button class="btn" disabled={busy || !ok} onClick={() => void add()}>Add</button>
        </div>
      </section>
    </main>
  );
}
